import { useEffect, useState } from "react";
import { getFirestore, collection, getDocs } from "firebase/firestore";
import { useNavigate, useLocation } from "react-router-dom";
import jsPDF from 'jspdf';
import app from "./firebase";
import styles from "./CentralInventoryDashboard.module.css";

const db = getFirestore(app);

export default function CentralInventoryDashboard() {
  const [ingredients, setIngredients] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all"); // Single filter dropdown
  const [sortBy, setSortBy] = useState("name"); // "name", "stock", "category"
  const [highlightedId, setHighlightedId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch all inventory data
  const fetchData = async () => {
    setLoading(true);
    try {
      const [ingSnap, itemSnap, storeSnap, vendorSnap] = await Promise.all([
        getDocs(collection(db, "ingredients")),
        getDocs(collection(db, "items")),
        getDocs(collection(db, "stores")),
        getDocs(collection(db, "vendors")),
      ]);
      
      const storesArr = storeSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const vendorsArr = vendorSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Process ingredients
      const ingredientsArr = ingSnap.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          type: 'ingredient',
          currentStock: data.currentStock || 0,
          minStock: data.minStock || 5,
          unit: data.unit || "units",
          lastUpdated: data.lastUpdated || null,
          supplierName: storesArr.find(s => s.id === data.storeId)?.name || "Unknown Store",
          supplierId: data.storeId,
          category: "Ingredient"
        };
      });

      // Process items
      const itemsArr = itemSnap.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          type: 'item',
          currentStock: data.currentStock || 0,
          minStock: data.minStock || 5,
          unit: data.unit || "pieces",
          lastUpdated: data.lastUpdated || null,
          supplierName: vendorsArr.find(v => v.id === data.vendorId)?.name || "Unknown Vendor",
          supplierId: data.vendorId,
          category: data.category || "Other"
        };
      });

      setIngredients(ingredientsArr);
      setItems(itemsArr);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Handle highlighting from search - triggers on any highlight event
  useEffect(() => {
    if (location.state?.highlightId && !loading && (ingredients.length > 0 || items.length > 0)) {
      setHighlightedId(location.state.highlightId);
      
      const scrollToElement = () => {
        const element = document.getElementById(`inventory-${location.state.highlightId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return true;
        }
        return false;
      };

      // Try immediately first
      if (!scrollToElement()) {
        // If element not found, wait a bit for DOM to update
        const timer = setTimeout(() => {
          scrollToElement();
        }, 100);
        setTimeout(() => setHighlightedId(null), 3000);
        return () => clearTimeout(timer);
      } else {
        setTimeout(() => setHighlightedId(null), 3000);
      }
    }
  }, [location.state, loading, ingredients, items]);

  // Filter and sort all inventory items
  const filteredAndSortedItems = () => {
    let allItems = getAllInventoryItems();
    
    // Apply search filter
    if (searchTerm) {
      allItems = allItems.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.supplierName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply type/stock filters
    switch (filterType) {
      case "ingredients":
        allItems = allItems.filter(item => item.type === 'ingredient');
        break;
      case "items":
        allItems = allItems.filter(item => item.type === 'item');
        break;
      case "low":
        allItems = allItems.filter(item => item.currentStock <= item.minStock && item.currentStock > 0);
        break;
      case "out":
        allItems = allItems.filter(item => item.currentStock === 0);
        break;
      case "instock":
        allItems = allItems.filter(item => item.currentStock > item.minStock);
        break;
      case "all":
      default:
        break;
    }

    // Apply sorting
    switch (sortBy) {
      case "name":
        allItems.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "stock":
        allItems.sort((a, b) => a.currentStock - b.currentStock);
        break;
      case "category":
        allItems.sort((a, b) => a.category.localeCompare(b.category));
        break;
      default:
        break;
    }

    return allItems;
  };

  // Combine ingredients and items for unified view
  const getAllInventoryItems = () => {
    return [...ingredients, ...items];
  };

  // Get low stock items
  const getLowStockItems = () => {
    return getAllInventoryItems().filter(item => 
      item.currentStock <= item.minStock
    );
  };

  // Get out of stock items
  const getOutOfStockItems = () => {
    return getAllInventoryItems().filter(item => 
      item.currentStock === 0
    );
  };

  // Get stock status
  const getStockStatus = (item) => {
    if (item.currentStock === 0) return "out";
    if (item.currentStock <= item.minStock) return "low";
    return "normal";
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "out": return "#ef4444"; // red
      case "low": return "#f59e0b"; // orange
      case "normal": return "#10b981"; // green
      default: return "#6b7280"; // gray
    }
  };

  // Generate comprehensive inventory report PDF
  const generateInventoryReportPDF = () => {
    const allItems = getAllInventoryItems();
    const lowStockItems = getLowStockItems();
    const outOfStockItems = getOutOfStockItems();
    
    const doc = new jsPDF();
    let yPosition = 20;
    
    // Title
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text('Complete Inventory Report', 20, yPosition);
    yPosition += 10;
    
    // Date and summary
    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Total Items: ${allItems.length} | Low Stock: ${lowStockItems.length} | Out of Stock: ${outOfStockItems.length}`, 20, yPosition);
    yPosition += 15;

    // Summary by category
    const categoryStats = {};
    allItems.forEach(item => {
      const category = item.type === 'ingredient' ? 'Ingredients' : item.category;
      if (!categoryStats[category]) {
        categoryStats[category] = { total: 0, lowStock: 0, outOfStock: 0 };
      }
      categoryStats[category].total++;
      if (item.currentStock === 0) categoryStats[category].outOfStock++;
      else if (item.currentStock <= item.minStock) categoryStats[category].lowStock++;
    });

    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Inventory Summary by Category', 20, yPosition);
    yPosition += 10;

    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    Object.entries(categoryStats).forEach(([category, stats]) => {
      const text = `${category}: ${stats.total} total, ${stats.lowStock} low stock, ${stats.outOfStock} out of stock`;
      doc.text(text, 25, yPosition);
      yPosition += 6;
    });
    yPosition += 10;

    // Critical items (out of stock)
    if (outOfStockItems.length > 0) {
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(220, 38, 38);
      doc.text('CRITICAL - Out of Stock Items', 20, yPosition);
      yPosition += 10;
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(0, 0, 0);
      outOfStockItems.forEach(item => {
        const text = `• ${item.name} (${item.type}) - ${item.supplierName}`;
        doc.text(text, 25, yPosition);
        yPosition += 6;
        if (yPosition > 270) {
          doc.addPage();
          yPosition = 20;
        }
      });
      yPosition += 10;
    }

    // Low stock items
    const lowStockOnly = lowStockItems.filter(item => item.currentStock > 0);
    if (lowStockOnly.length > 0) {
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(245, 158, 11);
      doc.text('Items Requiring Restock', 20, yPosition);
      yPosition += 10;
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(0, 0, 0);
      lowStockOnly.forEach(item => {
        const text = `• ${item.name} (${item.type}) - Current: ${item.currentStock}/${item.minStock} ${item.unit} - ${item.supplierName}`;
        doc.text(text, 25, yPosition);
        yPosition += 6;
        if (yPosition > 270) {
          doc.addPage();
          yPosition = 20;
        }
      });
    }

    const fileName = `inventory-report-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };

  // Calculate summary stats
  const stats = {
    totalItems: getAllInventoryItems().length,
    totalIngredients: ingredients.length,
    totalItemsOnly: items.length,
    lowStock: getLowStockItems().filter(item => item.currentStock > 0).length,
    outOfStock: getOutOfStockItems().length,
    inStock: getAllInventoryItems().filter(item => item.currentStock > item.minStock).length
  };

  if (loading) return <p>Loading inventory...</p>;

  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.header}>
        <h2 className={styles.dashboardTitle}>Central Inventory Overview</h2>
        <div className={styles.headerActions}>
          <button
            className={styles.manageButton}
            onClick={() => navigate("/ingredients")}
          >
            Manage Ingredients
          </button>
          <button
            className={styles.manageButton}
            onClick={() => navigate("/items")}
          >
            Manage Items
          </button>
        </div>
      </div>
      
      {/* Summary Statistics */}
      <div className={styles.statsContainer}>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>{stats.totalItems}</div>
          <div className={styles.statLabel}>Total Inventory Items</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>{stats.totalIngredients}</div>
          <div className={styles.statLabel}>Ingredients</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>{stats.totalItemsOnly}</div>
          <div className={styles.statLabel}>Items</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statNumber} style={{ color: "#10b981" }}>{stats.inStock}</div>
          <div className={styles.statLabel}>In Stock</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statNumber} style={{ color: "#f59e0b" }}>{stats.lowStock}</div>
          <div className={styles.statLabel}>Low Stock</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statNumber} style={{ color: "#ef4444" }}>{stats.outOfStock}</div>
          <div className={styles.statLabel}>Out of Stock</div>
        </div>
      </div>

      {/* Controls */}
      <div className={styles.topControls}>
        <div className={styles.searchContainer}>
          <input
            type="text"
            placeholder="Search all inventory items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        <div className={styles.filterContainer}>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Items</option>
            <option value="ingredients">Ingredients Only</option>
            <option value="items">Items Only</option>
            <option value="instock">In Stock</option>
            <option value="low">Low Stock</option>
            <option value="out">Out of Stock</option>
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className={styles.sortSelect}
          >
            <option value="name">Sort by Name</option>
            <option value="stock">Sort by Stock Level</option>
            <option value="category">Sort by Category</option>
          </select>
        </div>
        <button
          className={styles.reportButton}
          onClick={generateInventoryReportPDF}
        >
          📊 Generate Full Report PDF
        </button>
      </div>

      {/* Inventory Grid */}
      <div className={styles.cardGrid}>
        {filteredAndSortedItems().map(item => {
          const status = getStockStatus(item);
          const statusColor = getStatusColor(status);
          
          return (
            <div 
              key={`${item.type}-${item.id}`} 
              id={`inventory-${item.id}`}
              className={`${styles.inventoryCard} ${highlightedId === item.id ? styles.highlighted : ''}`}
            >
              <div className={styles.cardHeader}>
                <div className={styles.itemName}>{item.name}</div>
                <div className={styles.itemType}>{item.type === 'ingredient' ? '🥕' : '📦'}</div>
              </div>
              
              <div className={styles.cardContent}>
                <div className={styles.categoryBadge}>
                  {item.category}
                </div>
                
                <div className={styles.stockInfo}>
                  <div className={styles.stockCurrent}>
                    <span className={styles.stockLabel}>Current:</span>
                    <span className={styles.stockValue} style={{ color: statusColor }}>
                      {item.currentStock} {item.unit}
                    </span>
                  </div>
                  <div className={styles.stockMin}>
                    <span className={styles.stockLabel}>Minimum:</span>
                    <span className={styles.stockValue}>
                      {item.minStock} {item.unit}
                    </span>
                  </div>
                </div>
                
                <div 
                  className={styles.statusBadge}
                  style={{ backgroundColor: statusColor }}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)} Stock
                </div>
                
                <div className={styles.supplierInfo}>
                  <span className={styles.supplierIcon}>
                    {item.type === 'ingredient' ? '🏬' : '🏢'}
                  </span>
                  <span className={styles.supplierName}>{item.supplierName}</span>
                </div>
                
                {item.lastUpdated && (
                  <div className={styles.lastUpdated}>
                    Last updated: {new Date(
                      item.lastUpdated.seconds ? 
                      item.lastUpdated.seconds * 1000 : 
                      item.lastUpdated
                    ).toLocaleDateString()}
                  </div>
                )}
              </div>
              
              <div className={styles.cardActions}>
                <button
                  className={styles.manageButton}
                  onClick={() => navigate(
                    item.type === 'ingredient' ? '/ingredients' : '/items',
                    { state: { highlightId: item.id } }
                  )}
                >
                  Manage {item.type === 'ingredient' ? 'Ingredient' : 'Item'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {filteredAndSortedItems().length === 0 && (
        <div className={styles.noResults}>
          {searchTerm ? `No inventory items found matching "${searchTerm}"` : "No inventory items found"}
        </div>
      )}
    </div>
  );
}
